import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Printer, Download, ArrowLeft, CalendarDays } from "lucide-react";
import useSEO from "@/lib/useSEO";

/**
 * Shared layout for every legal document.
 * Handles: <title>, print-to-PDF button, effective date banner, print-CSS scoping.
 */
export default function LegalDoc({ title, effectiveDate, seoDescription, path, children }) {
  useSEO({ title: `${title} | Sarthi Associates`, description: seoDescription || `${title} — Sarthi Associates legal documentation.`, path });
  useEffect(() => {
    document.body.classList.add("legal-doc-body");
    return () => document.body.classList.remove("legal-doc-body");
  }, []);

  const printDoc = () => window.print();

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6" data-testid={`legal-${(path || "").replace("/", "")}`}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-sarthi-muted hover:text-sarthi-primary">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
        <div className="flex gap-2">
          <button
            data-testid={`print-${(path || "").replace("/", "")}`}
            onClick={printDoc}
            className="sarthi-btn-outline inline-flex items-center gap-2 px-4 py-2 text-sm font-bold"
            title="Print or Save as PDF"
          >
            <Printer className="h-4 w-4" /> Print / Save PDF
          </button>
        </div>
      </div>

      <article className="sarthi-card px-6 py-8 print:border-0 print:shadow-none sm:px-10 sm:py-12" data-testid="legal-doc-article">
        <header className="mb-6 border-b-2 border-sarthi-border pb-6">
          <div className="mb-2 text-xs font-extrabold uppercase tracking-widest text-sarthi-primary">Sarthi Associates · Legal</div>
          <h1 className="font-display text-3xl font-black text-sarthi-text sm:text-4xl">{title}</h1>
          <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-sarthi-muted">
            <CalendarDays className="h-4 w-4" />
            <span data-testid="legal-effective-date">Last updated: <b className="text-sarthi-text">{effectiveDate}</b></span>
          </div>
        </header>
        <div className="legal-body prose max-w-none">{children}</div>
        <footer className="mt-10 border-t border-sarthi-border pt-6 text-xs font-medium text-sarthi-muted">
          © {new Date().getFullYear()} Sarthi Associates. All rights reserved. · Head office: Noida, Uttar Pradesh, India · Queries: <a href="mailto:contact@sarthiassociates.com" className="font-bold text-sarthi-primary hover:underline">contact@sarthiassociates.com</a>
        </footer>
      </article>

      <style>{`
        .legal-body h2 { font-family: var(--sarthi-display, sans-serif); font-weight: 800; font-size: 1.5rem; color: var(--sarthi-text, #0F172A); margin-top: 2rem; margin-bottom: 0.75rem; border-bottom: 1px solid #E2E8F0; padding-bottom: 0.5rem; }
        .legal-body h3 { font-weight: 700; font-size: 1.05rem; color: #1F2937; margin-top: 1.5rem; margin-bottom: 0.5rem; }
        .legal-body p, .legal-body li { color: #334155; line-height: 1.65; font-size: 0.95rem; }
        .legal-body ol, .legal-body ul { padding-left: 1.5rem; margin: 0.5rem 0 1rem; }
        .legal-body li { margin: 0.25rem 0; }
        .legal-body strong { color: #0F172A; }
        .legal-body a { color: #E05D36; font-weight: 600; text-decoration: underline; }
        @media print {
          body.legal-doc-body header, body.legal-doc-body footer.site-footer, body.legal-doc-body .voice-btn { display: none !important; }
          .legal-body { font-size: 10.5pt; }
        }
      `}</style>
    </div>
  );
}
